import type { AcademyTrack } from '@platform/shared-types';

/**
 * Commits a just-saved certification back into the sidebar list, applying THE
 * EXCLUSIVITY RULE client-side when the save turned `activatesPartner` on.
 *
 * The server already reconciles this — `tracks.put` runs
 * `applyExclusiveActivation` and rewrites every other track that carried the
 * flag — but the client was never told. `ExamSettingsTab.publish()` spliced
 * only the saved track into local `tracks`, so the certification that had just
 * been DEMOTED kept its "Activates partners" marker until a full page reload.
 * That sidebar is the only readout of the "exactly one activating
 * certification" invariant anywhere in the admin UI, and it showed TWO at
 * precisely the moment an admin was exercising that invariant by moving the
 * flag — the one moment it most needs to be true.
 *
 * `exclusive` is the caller's load-guard verdict (`loadGuard.isCurrent(token)`
 * for the publish that produced `saved`). The row for `saved` itself is always
 * replaced — it is matched by `trackId`, so it can never land on the wrong row
 * — but the sweep across the OTHER rows is applied only while that publish is
 * still the newest operation, so a stale response can never reach across and
 * rewrite rows a newer operation already owns.
 *
 * Pure: no React, no fetch, no state — directly unit-testable without a React
 * testing library, which this repo does not have and is not allowed to add.
 * Same reasoning as `loadGuard.ts` alongside it.
 */
export function mergeSavedTrack(
  prev: AcademyTrack[],
  saved: AcademyTrack,
  exclusive: boolean,
): AcademyTrack[] {
  return prev.map((t) => {
    if (t.trackId === saved.trackId) return saved;
    if (exclusive && saved.activatesPartner === true && t.activatesPartner === true) {
      return { ...t, activatesPartner: false };
    }
    return t;
  });
}
